import { AGE_MIXES, COMP_SPREADS, ESTIMATOR_DEFAULTS } from './estimator.js'
import { extractFinalJsonObject } from './aiJson.js'

// Client side of the estimator's AI assist: send the advisor's free-text
// company description to /api/assistant/estimate, then clamp whatever comes
// back to the estimator's own input whitelist. The form is prefilled, never
// auto-submitted – the advisor still reviews every assumption.

const NUMBER_KEYS = new Set([
  'headcount', 'typicalComp', 'ownerCount', 'ownerComp',
  'participationRate', 'avgDeferralPercent', 'hceCount', 'partTimePercent', 'turnoverPercent',
])

const FALLBACK_NOTE = 'The AI response did not include usable structured assumptions, so default estimate assumptions were used.'

function fallbackAssist(rawNotes = []) {
  const notes = [
    FALLBACK_NOTE,
    ...rawNotes.filter((n) => typeof n === 'string').map((n) => n.slice(0, 200)),
  ].slice(0, 10)
  return { inputs: { ...ESTIMATOR_DEFAULTS }, notes }
}

export async function requestEstimateAssist(description) {
  // Dynamic import: supabaseClient reads import.meta.env (Vite-only) and the
  // sanitize half of this module runs under node --test.
  const { getSupabaseClient } = await import('./supabaseClient.js')
  const client = getSupabaseClient()
  const { data } = client ? await client.auth.getSession() : { data: null }
  const token = data?.session?.access_token
  if (!token) throw new Error('Sign in to use the AI assist.')

  const response = await fetch('/api/assistant/estimate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ description }),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) throw new Error(payload?.error || `AI assist failed (${response.status})`)
  return payload?.content ?? ''
}

export function parseEstimateAssistContent(content) {
  // The model may narrate reasoning around the answer; take the final JSON
  // object that actually carries inputs (see aiJson.js).
  const parsed = extractFinalJsonObject(content, { requiredKey: 'inputs' })
  return parsed ?? fallbackAssist()
}

// Keep only known estimator inputs with plausible values; exact clamping to
// ranges happens again in normalizeEstimatorInputs when the estimate starts.
export function sanitizeEstimateAssist(raw) {
  const inputs = {}
  const source = raw?.inputs && typeof raw.inputs === 'object' ? raw.inputs : {}
  for (const [key, value] of Object.entries(source)) {
    if (NUMBER_KEYS.has(key)) {
      const parsed = Number(String(value).replace(/[$,%\s]/g, ''))
      if (Number.isFinite(parsed) && parsed >= 0) inputs[key] = parsed
    } else if (key === 'compSpread' && COMP_SPREADS[value]) {
      inputs.compSpread = value
    } else if (key === 'ageMix' && AGE_MIXES[value]) {
      inputs.ageMix = value
    }
  }
  const notes = Array.isArray(raw?.notes)
    ? raw.notes.filter((n) => typeof n === 'string').map((n) => n.slice(0, 200)).slice(0, 10)
    : []
  if (Object.keys(inputs).length === 0) return fallbackAssist(notes)
  return { inputs, notes }
}
